import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const Teacherstudentassignment = ({ onNavigate, userInfo }) => {
    // Page Specific State
    const [isMobile, setIsMobile] = useState(false);

    // Page Specific State
    const [activeTab, setActiveTab] = useState('create'); // 'create', 'view', 'submissions'
    const [loading, setLoading] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [viewingPDF, setViewingPDF] = useState(null); // For PDF viewer
    const [editingMarks, setEditingMarks] = useState({}); // For inline marks editing
    const [viewStatus, setViewStatus] = useState({}); // Track view status: 'viewed' or 'pending'

    const [assignmentForm, setAssignmentForm] = useState({
        title: '',
        description: '',
        subject_id: '',
        batch_name: '',
        year: '',
        semester: '',
        dueDate: '',
        totalMarks: 100,
        attachments: []
    });

    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    // Teacher data
    const [teacherData, setTeacherData] = useState({
        name: userInfo?.name || 'Teacher',
        teacherId: userInfo?.id || 'T001',
        department: userInfo?.department || 'Computer Science',
        designation: userInfo?.designation || 'Lecturer',
        profileImage: userInfo?.profileImage || null
    });

    // Available options (Dynamic)
    const [availableBatches, setAvailableBatches] = useState([]);
    const [availableSubjects, setAvailableSubjects] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const semesters = ['1st Sem', '2nd Sem', '3rd Sem', '4th Sem', '5th Sem', '6th Sem', '7th Sem', '8th Sem'];

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        fetchAssignments();
    }, []);



    // --- Assignment Logic ---

    // API Helper Function
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token found');

        const response = await fetch(`${API_LINK}/api/teacher${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            if (response.status === 401) handleLogout();
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    };

    // Fetch Available Years
    const fetchAvailableYears = async (department) => {
        try {
            const result = await apiCall(`/attendance/years?department=${encodeURIComponent(department)}`);
            if (result.success) setAvailableYears(result.data);
        } catch (error) {
            console.error('Error fetching years:', error);
        }
    };

    // Fetch Batches
    const fetchBatches = async (department, year) => {
        try {
            const result = await apiCall(`/attendance/batches?department=${encodeURIComponent(department)}&year=${year}`);
            if (result.success) setAvailableBatches(result.data);
        } catch (error) {
            console.error('Error fetching batches:', error);
        }
    };

    // Fetch Subjects
    const fetchSubjects = async (department, year) => {
        try {
            const result = await apiCall(`/attendance/subjects?department=${encodeURIComponent(department)}&year=${year}`);
            if (result.success) setAvailableSubjects(result.data);
        } catch (error) {
            console.error('Error fetching subjects:', error);
        }
    };

    // Fetch assignments
    const fetchAssignments = async () => {
        try {
            setLoading(true);
            const result = await apiCall('/assignments');
            if (result.success) {
                setAssignments(result.assignments || []);
            }
        } catch (error) {
            console.error('Error fetching assignments:', error);
            loadDemoAssignments();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (teacherData.department) {
            fetchAvailableYears(teacherData.department);
        }
    }, [teacherData.department]);

    // Load demo assignments
    const loadDemoAssignments = () => {
        const demoAssignments = [
            {
                id: 1,
                title: 'Data Structures Assignment',
                description: 'Implement Binary Search Tree with all operations',
                subject: 'Computer Science',
                class: 'BCA 2nd Year',
                semester: 'Semester 3',
                dueDate: '2026-01-25',
                totalMarks: 100,
                createdAt: '2026-01-10',
                submissionsCount: 45,
                totalStudents: 50,
                status: 'active'
            },
            {
                id: 2,
                title: 'Database Management System',
                description: 'Design and implement a library management system database',
                subject: 'Computer Science',
                class: 'BCA 2nd Year',
                semester: 'Semester 4',
                dueDate: '2026-01-30',
                totalMarks: 100,
                createdAt: '2026-01-12',
                submissionsCount: 38,
                totalStudents: 50,
                status: 'active'
            },
            {
                id: 3,
                title: 'Web Development Project',
                description: 'Create a responsive portfolio website using HTML, CSS, and JavaScript',
                subject: 'Computer Science',
                class: 'BCA 1st Year',
                semester: 'Semester 2',
                dueDate: '2026-01-20',
                totalMarks: 50,
                createdAt: '2026-01-05',
                submissionsCount: 60,
                totalStudents: 60,
                status: 'completed'
            }
        ];
        setAssignments(demoAssignments);
    };

    // Handle form input change
    const handleInputChange = (field, value) => {
        setAssignmentForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleYearChange = (value) => {
        handleInputChange('year', value);
        handleInputChange('batch_name', '');
        handleInputChange('subject_id', '');
        setAvailableBatches([]);
        setAvailableSubjects([]);

        if (value && teacherData.department) {
            fetchBatches(teacherData.department, value);
            fetchSubjects(teacherData.department, value);
        }
    };

    // Handle file upload
    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setAssignmentForm(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...files]
        }));
    };

    // Remove attachment
    const removeAttachment = (index) => {
        setAssignmentForm(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };

    // Create assignment
    const handleCreateAssignment = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            const formData = new FormData();

            // Required backend fields: title, description, department, batch_name, subject_id, due_date, total_marks
            formData.append('title', assignmentForm.title);
            formData.append('description', assignmentForm.description);
            formData.append('department', teacherData.department);
            formData.append('batch_name', assignmentForm.batch_name);
            formData.append('subject_id', assignmentForm.subject_id);
            formData.append('due_date', assignmentForm.dueDate);
            formData.append('total_marks', assignmentForm.totalMarks);

            assignmentForm.attachments.forEach(file => {
                formData.append('attachments', file);
            });

            const result = await apiCall('/assignments/create', {
                method: 'POST',
                body: formData
            });

            if (result.success) {
                setSuccessMessage('✅ Assignment created successfully!');
                fetchAssignments();
                setAssignmentForm({
                    title: '',
                    description: '',
                    subject_id: '',
                    batch_name: '',
                    year: '',
                    semester: '',
                    dueDate: '',
                    totalMarks: 100,
                    attachments: []
                });
                setActiveTab('view');
                setTimeout(() => setSuccessMessage(null), 3000);
            } else {
                setErrorMessage('❌ ' + result.message);
                setTimeout(() => setErrorMessage(null), 3000);
            }
        } catch (error) {
            console.error('Error creating assignment:', error);
            setErrorMessage('❌ Failed to create assignment');
            setTimeout(() => setErrorMessage(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    // View submissions
    const viewSubmissions = async (assignment) => {
        // Normalize property names from snake_case (backend) to camelCase (frontend)
        const normalizedAssignment = {
            ...assignment,
            totalMarks: assignment.totalMarks || assignment.total_marks || 100,
            dueDate: assignment.dueDate || assignment.due_date
        };
        setSelectedAssignment(normalizedAssignment);
        setActiveTab('submissions');

        try {
            setLoading(true);
            const result = await apiCall(`/assignments/${assignment.id}/submissions`);
            if (result.success) {
                setSubmissions(result.submissions || []);
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
            setErrorMessage('❌ Failed to fetch submissions');
            setTimeout(() => setErrorMessage(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    // Check if submission is late
    const isLateSubmission = (submittedAt, dueDate) => {
        if (!submittedAt || !dueDate) return false;
        return new Date(submittedAt) > new Date(dueDate);
    };

    // Get submission status display
    const getSubmissionStatus = (submission, assignment) => {
        if (submission.status === 'not_submitted') {
            return { label: '❌ Not Submitted', color: '#dc3545', bgColor: '#f8d7da', border: '#f5c6cb' };
        }
        const isLate = isLateSubmission(submission.submittedAt, assignment.dueDate);
        if (isLate) {
            return { label: '⏰ Late Submitted', color: '#856404', bgColor: '#fff3cd', border: '#ffeaa7' };
        }
        return { label: '✓ Submitted', color: '#155724', bgColor: '#d4edda', border: '#c3e6cb' };
    };

    // Handle marks update
    const handleMarksUpdate = (submissionId, marks) => {
        setEditingMarks(prev => ({
            ...prev,
            [submissionId]: marks
        }));
    };

    // Save marks
    const saveMarks = async (submissionId) => {
        try {
            const marks = editingMarks[submissionId];
            const feedback = ''; // You could add a feedback input later

            const result = await apiCall('/assignments/grade', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ submission_id: submissionId, marks, feedback })
            });

            if (result.success) {
                setSuccessMessage('✅ Marks saved successfully!');
                // Refetch submissions to update state
                viewSubmissions(selectedAssignment);

                // Clear editing state
                setEditingMarks(prev => {
                    const newState = { ...prev };
                    delete newState[submissionId];
                    return newState;
                });
                setTimeout(() => setSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.error('Error saving marks:', error);
            setErrorMessage('❌ Failed to save marks');
            setTimeout(() => setErrorMessage(null), 3000);
        }
    };

    // View PDF
    const viewPDF = (submission) => {
        setViewingPDF(submission);
    };

    // Mark submission as viewed
    const markAsViewed = (submissionId) => {
        setViewStatus(prev => ({
            ...prev,
            [submissionId]: 'viewed'
        }));
        setViewingPDF(null); // Close modal
        setSuccessMessage('✅ Marked as viewed!');
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    // Mark submission as pending
    const markAsPending = (submissionId) => {
        setViewStatus(prev => ({
            ...prev,
            [submissionId]: 'pending'
        }));
        setViewingPDF(null); // Close modal
        setSuccessMessage('⏳ Marked as pending!');
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    // Download submissions report as CSV
    const downloadReport = () => {
        if (!selectedAssignment || submissions.length === 0) {
            setErrorMessage('❌ No submissions to download');
            setTimeout(() => setErrorMessage(null), 3000);
            return;
        }

        // Prepare CSV headers
        const headers = ['Student Name', 'Student ID', 'Submitted At', 'Status', 'Marks', 'Action'];

        // Prepare CSV rows
        const rows = submissions.map(submission => {
            const statusInfo = getSubmissionStatus(submission, selectedAssignment);
            const submittedDate = submission.submittedAt
                ? new Date(submission.submittedAt).toLocaleString('en-GB')
                : '-';
            const marks = submission.marks !== null
                ? `${submission.marks}/${selectedAssignment.totalMarks}`
                : '-';
            const action = viewStatus[submission.id] === 'viewed' ? 'Viewed' : 'Pending';

            return [
                submission.studentName,
                submission.studentId,
                submittedDate,
                statusInfo.label.replace(/[✓❌⏰⏳]/g, '').trim(), // Remove emojis for CSV
                marks,
                action
            ];
        });

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `${selectedAssignment.title}_submissions_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSuccessMessage('✅ Report downloaded successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return '#28a745';
            case 'completed': return '#007bff';
            case 'overdue': return '#dc3545';
            default: return '#6c757d';
        }
    };

    const handleLogout = () => {
        // Clear tokens/session
        localStorage.clear();
        sessionStorage.clear();
        if (onNavigate) {
            onNavigate('home');
        } else {
            window.location.href = '/';
        }
    };

    return (
        <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-student-assignment" userInfo={userInfo}>
            {/* Success/Error Messages */}
            {(successMessage || errorMessage) && (
                <div style={{
                    background: successMessage ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${successMessage ? '#c3e6cb' : '#f5c6cb'}`,
                    color: successMessage ? '#155724' : '#721c24',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span>{successMessage || errorMessage}</span>
                    <button
                        onClick={() => {
                            setSuccessMessage(null);
                            setErrorMessage(null);
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Header Card */}
            <div style={{
                background: '#ffffff',
                borderRadius: '10px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                marginBottom: isMobile ? '0.75rem' : '1.25rem',
                overflow: 'hidden',
                border: '1px solid #eef0f2'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #238548, #1a6b38)',
                    color: '#ffffff',
                    padding: isMobile ? '1.25rem 1rem' : '1.5rem 2rem',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: isMobile ? 'center' : 'space-between',
                    alignItems: 'center',
                    gap: isMobile ? '0.6rem' : '1rem'
                }}>
                    <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                        <h1 style={{
                            margin: '0 0 0.25rem 0',
                            fontSize: isMobile ? '1.25rem' : '1.8rem',
                            fontWeight: 'bold',
                            letterSpacing: '-0.01em'
                        }}>
                            📝 Assignment Management
                        </h1>
                        <p style={{
                            margin: 0,
                            fontSize: isMobile ? '0.75rem' : '0.95rem',
                            opacity: 0.9
                        }}>
                            Create, assign, and manage student assignments
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    borderBottom: '1px solid #ecf0f1'
                }}>
                    {[
                        { id: 'create', label: 'Create Assignment', icon: '➕' },
                        { id: 'view', label: 'View Assignments', icon: '📋' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: 1,
                                background: activeTab === tab.id ? '#238548' : 'transparent',
                                color: activeTab === tab.id ? '#ffffff' : '#666',
                                border: 'none',
                                padding: isMobile ? '0.75rem 0.5rem' : '1rem',
                                cursor: 'pointer',
                                fontSize: isMobile ? '0.8rem' : '1rem',
                                fontWeight: '500',
                                transition: 'all 0.3s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.35rem'
                            }}
                        >
                            <span>{tab.icon}</span>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'create' && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '10px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                    padding: isMobile ? '1rem' : '2rem',
                    border: '1px solid #eef0f2'
                }}>
                    <h2 style={{
                        margin: isMobile ? '0 0 1rem 0' : '0 0 1.5rem 0',
                        color: '#238548',
                        fontSize: isMobile ? '1.15rem' : '1.5rem'
                    }}>
                        ➕ Create New Assignment
                    </h2>

                    <div style={{
                        background: '#e8f5e9',
                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                        borderRadius: '8px',
                        marginBottom: isMobile ? '1rem' : '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        border: '1px solid #c8e6c9'
                    }}>
                        <span style={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>🏫</span>
                        <span style={{ fontWeight: '600', color: '#2e7d32', fontSize: isMobile ? '0.75rem' : '0.95rem' }}>Department: {teacherData.department}</span>
                        <span style={{ color: '#666', fontSize: '0.75rem' }}>(auto-filled)</span>
                    </div>

                    <form onSubmit={handleCreateAssignment}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            gap: isMobile ? '0.75rem' : '1.5rem',
                            marginBottom: isMobile ? '1rem' : '1.5rem'
                        }}>
                            {/* Title */}
                            <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Assignment Title <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={assignmentForm.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    placeholder="Enter assignment title"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>

                            {/* Year */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Year <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <select
                                    value={assignmentForm.year}
                                    onChange={(e) => handleYearChange(e.target.value)}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none',
                                        color: '#333',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <option value="" style={{ color: '#333', backgroundColor: '#fff' }}>Select Year</option>
                                    {availableYears.map((year, index) => (
                                        <option key={index} value={year.value} style={{ color: '#333', backgroundColor: '#fff' }}>{year.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Batch */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Batch <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <select
                                    value={assignmentForm.batch_name}
                                    onChange={(e) => handleInputChange('batch_name', e.target.value)}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none',
                                        color: '#333',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <option value="" style={{ color: '#333', backgroundColor: '#fff' }}>Select Batch</option>
                                    {availableBatches.map((batch, index) => (
                                        <option key={index} value={batch} style={{ color: '#333', backgroundColor: '#fff' }}>{batch}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Subject */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Subject <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <select
                                    value={assignmentForm.subject_id}
                                    onChange={(e) => handleInputChange('subject_id', e.target.value)}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none',
                                        color: '#333',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <option value="" style={{ color: '#333', backgroundColor: '#fff' }}>Select Subject</option>
                                    {availableSubjects.map((sub, index) => (
                                        <option key={index} value={sub.subject_code} style={{ color: '#333', backgroundColor: '#fff' }}>{sub.subject_name} ({sub.subject_code})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Semester (Optional) */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Semester <span style={{ color: '#999', fontSize: '0.75rem' }}>(optional)</span>
                                </label>
                                <select
                                    value={assignmentForm.semester}
                                    onChange={(e) => handleInputChange('semester', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none',
                                        color: '#333',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <option value="" style={{ color: '#333', backgroundColor: '#fff' }}>Select Semester</option>
                                    {semesters.map((sem, index) => (
                                        <option key={index} value={sem} style={{ color: '#333', backgroundColor: '#fff' }}>{sem}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Due Date */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Due Date <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    value={assignmentForm.dueDate}
                                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>

                            {/* Total Marks */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Total Marks <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <input
                                    type="number"
                                    value={assignmentForm.totalMarks}
                                    onChange={(e) => handleInputChange('totalMarks', e.target.value)}
                                    placeholder="100"
                                    required
                                    min="1"
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>

                            {/* Description */}
                            <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Description <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <textarea
                                    value={assignmentForm.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Enter detailed assignment description..."
                                    required
                                    rows={5}
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        outline: 'none',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* File Upload */}
                            <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.4rem',
                                    fontWeight: '600',
                                    color: '#444',
                                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                                }}>
                                    Attachments (Optional)
                                </label>
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '0.5rem 0.75rem' : '0.75rem',
                                        border: '1.5px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: isMobile ? '0.85rem' : '1rem'
                                    }}
                                />
                                {assignmentForm.attachments.length > 0 && (
                                    <div style={{ marginTop: '1rem' }}>
                                        {assignmentForm.attachments.map((file, index) => (
                                            <div key={index} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '0.5rem',
                                                background: '#f8f9fa',
                                                borderRadius: '6px',
                                                marginBottom: '0.5rem'
                                            }}>
                                                <span style={{ fontSize: '0.85rem' }}>📎 {file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAttachment(index)}
                                                    style={{
                                                        background: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                background: loading ? '#95a5a6' : '#238548',
                                color: '#ffffff',
                                border: 'none',
                                padding: isMobile ? '0.75rem 1.5rem' : '1rem 2rem',
                                borderRadius: '8px',
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                fontWeight: '500',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                width: isMobile ? '100%' : 'auto'
                            }}
                        >
                            {loading ? '⏳ Creating...' : '✅ Create Assignment'}
                        </button>
                    </form>
                </div>
            )}

            {activeTab === 'view' && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: isMobile ? '0.75rem' : '1.5rem'
                }}>
                    {assignments.map((assignment) => (
                        <div key={assignment.id} style={{
                            background: '#ffffff',
                            borderRadius: '10px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                            border: '1px solid #eef2f6',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
                            }}
                        >
                            <div style={{
                                background: getStatusColor(assignment.status),
                                color: '#ffffff',
                                padding: isMobile ? '1rem' : '1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem', fontWeight: '600' }}>{assignment.title}</h3>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: isMobile ? '0.75rem' : '0.9rem', opacity: 0.9 }}>
                                        {assignment.subject_name || assignment.subject}
                                    </p>
                                </div>
                                <span style={{
                                    padding: '0.2rem 0.6rem',
                                    borderRadius: '20px',
                                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                                    fontWeight: '600',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                                }}>
                                    {assignment.status.toUpperCase()}
                                </span>
                            </div>

                            <div style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
                                <p style={{
                                    margin: '0 0 1rem 0',
                                    color: '#666',
                                    fontSize: isMobile ? '0.85rem' : '0.95rem',
                                    lineHeight: 1.5
                                }}>
                                    {assignment.description}
                                </p>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '1rem',
                                    marginBottom: '1rem'
                                }}>
                                    <div>
                                        <span style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: '#999',
                                            marginBottom: '0.2rem'
                                        }}>
                                            Batch
                                        </span>
                                        <span style={{
                                            fontSize: isMobile ? '0.85rem' : '1rem',
                                            fontWeight: '600',
                                            color: '#333'
                                        }}>
                                            {assignment.batch_name}
                                        </span>
                                    </div>
                                    <div>
                                        <span style={{
                                            display: 'block',
                                            fontSize: '0.75rem',
                                            color: '#999',
                                            marginBottom: '0.2rem'
                                        }}>
                                            Due Date
                                        </span>
                                        <span style={{
                                            fontSize: isMobile ? '0.85rem' : '1rem',
                                            fontWeight: '600',
                                            color: '#333'
                                        }}>
                                            {new Date(assignment.due_date || assignment.dueDate).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>

                                <div style={{
                                    background: '#f8f9fa',
                                    padding: '0.6rem 0.75rem',
                                    borderRadius: '6px',
                                    marginBottom: '1rem',
                                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                                    color: '#666'
                                }}>
                                    <strong>Status:</strong> {assignment.status || 'active'}
                                </div>

                                <button
                                    onClick={() => viewSubmissions(assignment)}
                                    style={{
                                        width: '100%',
                                        background: '#238548',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '0.6rem 1rem',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                                        fontWeight: '500',
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    📊 View Submissions
                                </button>
                            </div>
                        </div>
                    ))}

                    {assignments.length === 0 && (
                        <div style={{
                            gridColumn: '1 / -1',
                            textAlign: 'center',
                            padding: '3rem',
                            background: '#ffffff',
                            borderRadius: '10px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                            border: '1px solid #eef0f2'
                        }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
                            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.2rem' }}>No Assignments Yet</h3>
                            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Create your first assignment to get started</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'submissions' && selectedAssignment && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '10px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                    padding: isMobile ? '1rem' : '2rem',
                    border: '1px solid #eef0f2'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: isMobile ? 'stretch' : 'center',
                        justifyContent: 'space-between',
                        marginBottom: '2rem',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: '1rem'
                    }}>
                        <div>
                            <h2 style={{ margin: '0 0 0.5rem 0', color: '#238548', fontSize: '1.5rem' }}>
                                {selectedAssignment.title}
                            </h2>
                            <p style={{ margin: 0, color: '#666' }}>
                                {selectedAssignment.batch_name} • {selectedAssignment.subject_name || selectedAssignment.subject}
                            </p>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: '1rem',
                            width: isMobile ? '100%' : 'auto'
                        }}>
                            <button
                                onClick={downloadReport}
                                style={{
                                    background: '#238548',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.3s',
                                    width: isMobile ? '100%' : 'auto'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#1e6f3c'}
                                onMouseLeave={(e) => e.target.style.background = '#238548'}
                            >
                                📥 Download Report
                            </button>
                            <button
                                onClick={() => setActiveTab('view')}
                                style={{
                                    background: '#6c757d',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    width: isMobile ? '100%' : 'auto',
                                    textAlign: 'center'
                                }}
                            >
                                ← Back to Assignments
                            </button>
                        </div>
                    </div>

                    {isMobile ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {submissions.map((submission) => {
                                const statusInfo = getSubmissionStatus(submission, selectedAssignment);
                                const isNotSubmitted = submission.status === 'not_submitted';
                                return (
                                    <div
                                        key={submission.id}
                                        style={{
                                            background: '#ffffff',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '10px',
                                            padding: '1.25rem',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem'
                                        }}
                                    >
                                        {/* Student Identity */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#333', fontWeight: '600' }}>
                                                    {submission.studentName}
                                                </h4>
                                                <span style={{ fontSize: '0.85rem', color: '#666', fontFamily: 'monospace' }}>
                                                    ID: {submission.studentId}
                                                </span>
                                            </div>
                                            <span style={{
                                                padding: '0.3rem 0.75rem',
                                                borderRadius: '20px',
                                                fontSize: '0.7rem',
                                                fontWeight: '600',
                                                textTransform: 'uppercase',
                                                backgroundColor: statusInfo.bgColor,
                                                color: statusInfo.color,
                                                border: `1px solid ${statusInfo.border}`
                                            }}>
                                                {statusInfo.label}
                                            </span>
                                        </div>

                                        {/* Dates */}
                                        <div style={{ fontSize: '0.9rem', color: '#555' }}>
                                            <strong>Submitted:</strong>{' '}
                                            {submission.submittedAt ? (
                                                new Date(submission.submittedAt).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                            ) : (
                                                <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                            )}
                                        </div>

                                        {/* Marks & View status */}
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem',
                                            padding: '0.75rem 0',
                                            borderTop: '1px dashed #dee2e6',
                                            borderBottom: '1px dashed #dee2e6'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontWeight: '500', fontSize: '0.9rem', color: '#333' }}>Marks:</span>
                                                {!isNotSubmitted ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={selectedAssignment.totalMarks}
                                                            value={editingMarks[submission.id] !== undefined ? editingMarks[submission.id] : (submission.marks || '')}
                                                            onChange={(e) => handleMarksUpdate(submission.id, e.target.value)}
                                                            placeholder="0"
                                                            style={{
                                                                width: '60px',
                                                                padding: '0.4rem',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '4px',
                                                                textAlign: 'center',
                                                                fontSize: '0.9rem',
                                                                fontWeight: '600'
                                                            }}
                                                        />
                                                        <span style={{ color: '#999', fontSize: '0.9rem' }}>/{selectedAssignment.totalMarks}</span>
                                                        {editingMarks[submission.id] !== undefined && editingMarks[submission.id] !== (submission.marks || '').toString() && (
                                                            <button
                                                                onClick={() => saveMarks(submission.id)}
                                                                style={{
                                                                    background: '#238548',
                                                                    color: '#ffffff',
                                                                    border: 'none',
                                                                    padding: '0.4rem 0.8rem',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: '600'
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9rem' }}>-</span>
                                                )}
                                            </div>

                                            {!isNotSubmitted && (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontWeight: '500', fontSize: '0.9rem', color: '#333' }}>Status:</span>
                                                    <span style={{
                                                        padding: '0.25rem 0.6rem',
                                                        borderRadius: '15px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '600',
                                                        backgroundColor: viewStatus[submission.id] === 'viewed' ? '#d4edda' : '#fff3cd',
                                                        color: viewStatus[submission.id] === 'viewed' ? '#155724' : '#856404',
                                                        border: `1px solid ${viewStatus[submission.id] === 'viewed' ? '#c3e6cb' : '#ffeaa7'}`
                                                    }}>
                                                        {viewStatus[submission.id] === 'viewed' ? '✓ Viewed' : '⏳ Pending'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div>
                                            {!isNotSubmitted ? (
                                                <button
                                                    onClick={() => viewPDF(submission)}
                                                    style={{
                                                        width: '100%',
                                                        background: '#6c757d',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        padding: '0.6rem 1rem',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '600',
                                                        transition: 'all 0.3s',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    👁️ View Submission
                                                </button>
                                            ) : (
                                                <span style={{
                                                    color: '#999',
                                                    fontSize: '0.85rem',
                                                    fontStyle: 'italic',
                                                    display: 'block',
                                                    textAlign: 'center'
                                                }}>
                                                    No submission files
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{
                            overflowX: 'auto',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px'
                        }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: '#ffffff'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: 'linear-gradient(135deg, #238548 0%, #2d9e5a 100%)',
                                        color: '#ffffff'
                                    }}>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            border: '1px solid #1a6b38',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            letterSpacing: '0.5px'
                                        }}>Student Name</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            border: '1px solid #1a6b38',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            letterSpacing: '0.5px'
                                        }}>Student ID</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            border: '1px solid #1a6b38',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            letterSpacing: '0.5px'
                                        }}>Submitted At</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            border: '1px solid #1a6b38',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            letterSpacing: '0.5px'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            border: '1px solid #1a6b38',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            letterSpacing: '0.5px'
                                        }}>Marks</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            border: '1px solid #1a6b38',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            letterSpacing: '0.5px'
                                        }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {submissions.map((submission, index) => (
                                        <tr
                                            key={submission.id}
                                            style={{
                                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa'}
                                        >
                                            <td style={{
                                                padding: '1rem',
                                                border: '1px solid #dee2e6',
                                                fontWeight: '500',
                                                color: '#333'
                                            }}>{submission.studentName}</td>
                                            <td style={{
                                                padding: '1rem',
                                                border: '1px solid #dee2e6',
                                                color: '#666',
                                                fontFamily: 'monospace'
                                            }}>{submission.studentId}</td>
                                            <td style={{
                                                padding: '1rem',
                                                border: '1px solid #dee2e6',
                                                color: '#666'
                                            }}>
                                                {submission.submittedAt ? (
                                                    new Date(submission.submittedAt).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })
                                                ) : (
                                                    <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{
                                                padding: '1rem',
                                                border: '1px solid #dee2e6',
                                                textAlign: 'center'
                                            }}>
                                                <span style={{
                                                    padding: '0.4rem 0.9rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    backgroundColor: getSubmissionStatus(submission, selectedAssignment).bgColor,
                                                    color: getSubmissionStatus(submission, selectedAssignment).color,
                                                    border: `1px solid ${getSubmissionStatus(submission, selectedAssignment).border}`
                                                }}>
                                                    {getSubmissionStatus(submission, selectedAssignment).label}
                                                </span>
                                            </td>
                                            <td style={{
                                                padding: '1rem',
                                                border: '1px solid #dee2e6',
                                                textAlign: 'center'
                                            }}>
                                                {submission.status !== 'not_submitted' ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={selectedAssignment.totalMarks}
                                                            value={editingMarks[submission.id] !== undefined ? editingMarks[submission.id] : (submission.marks || '')}
                                                            onChange={(e) => handleMarksUpdate(submission.id, e.target.value)}
                                                            placeholder="0"
                                                            style={{
                                                                width: '60px',
                                                                padding: '0.4rem',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '4px',
                                                                textAlign: 'center',
                                                                fontSize: '0.9rem',
                                                                fontWeight: '600'
                                                            }}
                                                        />
                                                        <span style={{ color: '#999' }}>/{selectedAssignment.totalMarks}</span>
                                                        {editingMarks[submission.id] !== undefined && editingMarks[submission.id] !== (submission.marks || '').toString() && (
                                                            <button
                                                                onClick={() => saveMarks(submission.id)}
                                                                style={{
                                                                    background: '#238548',
                                                                    color: '#ffffff',
                                                                    border: 'none',
                                                                    padding: '0.4rem 0.8rem',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: '600'
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{
                                                padding: '1rem',
                                                border: '1px solid #dee2e6',
                                                textAlign: 'center'
                                            }}>
                                                {submission.status !== 'not_submitted' ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                                                        {/* View Status Badge */}
                                                        <span style={{
                                                            padding: '0.3rem 0.8rem',
                                                            borderRadius: '15px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600',
                                                            backgroundColor: viewStatus[submission.id] === 'viewed' ? '#d4edda' : '#fff3cd',
                                                            color: viewStatus[submission.id] === 'viewed' ? '#155724' : '#856404',
                                                            border: `1px solid ${viewStatus[submission.id] === 'viewed' ? '#c3e6cb' : '#ffeaa7'}`
                                                        }}>
                                                            {viewStatus[submission.id] === 'viewed' ? '✓ Viewed' : '⏳ Pending'}
                                                        </span>

                                                        {/* View Button */}
                                                        <button
                                                            onClick={() => viewPDF(submission)}
                                                            style={{
                                                                background: '#6c757d',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '0.5rem 1rem',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600',
                                                                transition: 'all 0.3s',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                width: '100px'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.transform = 'translateY(-2px)';
                                                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.transform = 'translateY(0)';
                                                                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                            }}
                                                        >
                                                            👁️ View
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        color: '#999',
                                                        fontSize: '0.85rem',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        No submission
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {submissions.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '3rem',
                            color: '#666'
                        }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                            <p>No submissions yet</p>
                        </div>
                    )}
                </div>
            )}


            {/* PDF Viewer Modal */}
            {viewingPDF && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: isMobile ? '1rem' : '2rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        maxWidth: '900px',
                        width: isMobile ? '95%' : '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        position: 'relative'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #238548 0%, #2d9e5a 100%)',
                            color: '#ffffff',
                            padding: '1.5rem',
                            borderRadius: '12px 12px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                                    📄 Submission Details
                                </h2>
                                <p style={{ margin: 0, opacity: 0.9 }}>
                                    {viewingPDF.studentName} ({viewingPDF.studentId})
                                </p>
                            </div>
                            <button
                                onClick={() => setViewingPDF(null)}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    color: '#ffffff',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div style={{ padding: isMobile ? '1.25rem' : '2rem' }}>
                            {/* Submission Info */}
                            <div style={{
                                background: '#f8f9fa',
                                padding: '1rem',
                                borderRadius: '8px',
                                marginBottom: '1.5rem',
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1rem'
                            }}>
                                <div>
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
                                        Submitted At
                                    </div>
                                    <div style={{ fontWeight: '600', color: '#333' }}>
                                        {new Date(viewingPDF.submittedAt).toLocaleString('en-GB')}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
                                        Status
                                    </div>
                                    <div style={{ fontWeight: '600', color: '#333' }}>
                                        {getSubmissionStatus(viewingPDF, selectedAssignment).label}
                                    </div>
                                </div>
                                {viewingPDF.marks !== null && (
                                    <div>
                                        <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
                                            Marks
                                        </div>
                                        <div style={{ fontWeight: '600', color: '#238548', fontSize: '1.2rem' }}>
                                            {viewingPDF.marks}/{selectedAssignment.totalMarks}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Submitted Document Download */}
                            {viewingPDF.attachments && viewingPDF.attachments.length > 0 ? (
                                <div style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    flexWrap: 'wrap'
                                }}>
                                    {viewingPDF.attachments.map((att, idx) => (
                                        <a
                                            key={idx}
                                            href={`${API_LINK}${att}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem',
                                                background: '#238548',
                                                color: '#fff',
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '8px',
                                                textDecoration: 'none',
                                                fontSize: '0.95rem',
                                                fontWeight: '600',
                                                transition: 'all 0.3s',
                                                boxShadow: '0 2px 6px rgba(35, 133, 72, 0.3)',
                                                width: isMobile ? '100%' : 'auto'
                                            }}
                                        >
                                            📥 Download {viewingPDF.attachments.length > 1 ? `File ${idx + 1}` : 'Submission'}
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ margin: 0, color: '#999', fontStyle: 'italic' }}>
                                    No attached files
                                </p>
                            )}

                            {viewingPDF.feedback && (
                                <div style={{
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    background: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '8px'
                                }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#856404' }}>
                                        📝 Feedback:
                                    </div>
                                    <div style={{ color: '#856404' }}>
                                        {viewingPDF.feedback}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div style={{
                                marginTop: '2rem',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: '1rem',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <button
                                    onClick={() => markAsViewed(viewingPDF.id)}
                                    style={{
                                        background: '#238548',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '0.75rem 2rem',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        transition: 'all 0.3s',
                                        boxShadow: '0 2px 6px rgba(35, 133, 72, 0.3)',
                                        flex: isMobile ? 'none' : 1,
                                        width: isMobile ? '100%' : 'auto',
                                        maxWidth: '200px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(35, 133, 72, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 2px 6px rgba(35, 133, 72, 0.3)';
                                    }}
                                >
                                    ✓ Mark as Viewed
                                </button>
                                <button
                                    onClick={() => markAsPending(viewingPDF.id)}
                                    style={{
                                        background: '#ff9800',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '0.75rem 2rem',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        transition: 'all 0.3s',
                                        boxShadow: '0 2px 6px rgba(255, 152, 0, 0.3)',
                                        flex: isMobile ? 'none' : 1,
                                        width: isMobile ? '100%' : 'auto',
                                        maxWidth: '200px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(255, 152, 0, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 2px 6px rgba(255, 152, 0, 0.3)';
                                    }}
                                >
                                    ⏳ Mark as Pending
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </Layout>
    );
};

export default Teacherstudentassignment;